import {useMessage} from '@coolcolduk/ui-mui';
import {useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router';
import {z} from 'zod';
import {Question, QuestionStatus} from '@cp/model';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import usePagination from '../../../hooks/helper/usePagination';
import {useSearchParamsHandler} from '../../../hooks/helper/useSearchParamsHandler';
import {useCountQuestionsFetch} from '../../../hooks/question/useCountQuestions';
import {ListQuestionsSingleTopicVariables, useListQuestions} from '../../../hooks/question/useListQuestions';
import useQuestionFilters from '../../../components/QuestionFilters/useQuestionFilters';

const paginationParamsSchema = z.object({
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).optional(),
});

type PaginationParams = z.infer<typeof paginationParamsSchema>;

export interface UseAdminQuestionPageOptions {
  defaultFiltersExpanded?: boolean;
}

export default function useAdminQuestionPage(options?: UseAdminQuestionPageOptions) {
  const {defaultFiltersExpanded = false} = options || {};
  const navigate = useNavigate();
  const {examModules, loading: modulesLoading} = useListExamModules();
  const {showMessage} = useMessage();

  // Pagination params only
  const {searchParams: paginationParams, setSearchParams: setPaginationParams} =
    useSearchParamsHandler<PaginationParams>({
      schema: paginationParamsSchema,
      defaultValues: {
        offset: 0,
        limit: 10,
      },
    });

  const {questions, loading: questionsLoading, error, fetchQuestions} = useListQuestions();
  const {filters, setFilters, QuestionFiltersComponent, availableTopics, setFiltersExpanded} = useQuestionFilters({
    examModules,
    modulesLoading,
    disabled: questionsLoading,
    statusAllowAll: true,
    statusAvailableStatuses: Object.values(QuestionStatus),
    defaultExpanded: defaultFiltersExpanded,
    useUrlParams: true, // Enable URL params persistence
  });

  const totalItems = useCountQuestionsFetch(
    filters.moduleId,
    filters.topic ? [filters.topic] : undefined,
    filters.difficulty ? [filters.difficulty] : undefined,
    filters.status ? [filters.status] : undefined,
  );

  const pagination = usePagination({
    pageSize: paginationParams.limit || 10,
    totalItems,
    initialPage: paginationParams.offset ? Math.floor(paginationParams.offset / (paginationParams.limit || 10)) + 1 : 1,
    handlePageChange: (startIndex, _endIndex, currentPageSize) => {
      if (filters.moduleId) {
        setPaginationParams({offset: startIndex, limit: currentPageSize});
        fetchQuestions({
          moduleId: filters.moduleId,
          topic: filters.topic || undefined,
          difficulty: filters.difficulty || undefined,
          status: filters.status || undefined,
          limit: currentPageSize,
          offset: startIndex,
        }).catch((error) => {
          if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
            return;
          }
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch questions';
          showMessage(errorMessage, 3000, 'error');
        });
      }
    },
  });

  useEffect(() => {
    if (error) {
      console.error('Error fetching questions:', error);
    }
  }, [error]);

  const updateFilters = useCallback(
    (data: Partial<Omit<ListQuestionsSingleTopicVariables, 'limit' | 'offset'>>) => {
      setFilters((prev) => ({
        ...prev,
        ...data,
      }));
      pagination.gotoPage(1);
    },
    [setFilters, pagination],
  );

  const handleModuleChange = (moduleId: string) => {
    updateFilters({moduleId, topic: undefined, difficulty: undefined, status: undefined});
  };

  const handleTopicChange = (topic?: string) => {
    updateFilters({topic});
  };

  const handleDifficultyChange = (difficulty?: number) => {
    updateFilters({difficulty});
  };

  const handleStatusChange = (status?: QuestionStatus) => {
    updateFilters({status});
  };

  const handleFetchQuestions = useCallback(async () => {
    if (!filters.moduleId) return;

    fetchQuestions({
      moduleId: filters.moduleId,
      topic: filters.topic || undefined,
      difficulty: filters.difficulty || undefined,
      status: filters.status || undefined,
      limit: pagination.pageSize,
      offset: pagination.startIndex,
    })
      .then(() => {
        setFiltersExpanded(false);
      })
      .catch((error) => {
        // Ignore AbortError - it's expected when requests are cancelled but the network request may still succeed
        // Apollo Client may abort requests due to React Strict Mode or component re-renders
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          return;
        }
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch questions';
        showMessage(errorMessage, 3000, 'error');
      });
  }, [filters, pagination, fetchQuestions, showMessage, setFiltersExpanded]);

  const handleQuestionClick = useCallback(
    (question: Question) => {
      // Navigate to question view page with question and filters in state
      // Current search params are already in the URL, no need to manually build query string
      navigate(`/admin/question/${question.id}`, {
        state: {
          question,
          filters,
          pagination: {
            currentPage: pagination.currentPage,
            pageSize: pagination.pageSize,
          },
        },
      });
    },
    [filters, pagination, navigate],
  );

  const handleCreateNewQuestion = useCallback(() => {
    navigate('/admin/question/new');
  }, [navigate]);

  const handlePageChange = useCallback(
    (page: number) => {
      pagination.gotoPage(page);
      // URL will be synced by the useEffect that watches pagination.currentPage
    },
    [pagination],
  );

  return {
    examModules,
    questions,
    filters,
    availableTopics,
    pagination,
    totalItems,
    questionsLoading,
    modulesLoading,
    handleModuleChange,
    handleTopicChange,
    handleDifficultyChange,
    handleStatusChange,
    handleFetchQuestions,
    handleQuestionClick,
    handleCreateNewQuestion,
    handlePageChange,
    QuestionFiltersComponent,
  };
}
